import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { inr, fileSize, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import CadViewer from "@/components/CadViewer";
import AngleGallery from "@/components/AngleGallery";
import { FormatChips, Spec } from "@/components/Badges";
import { toast } from "sonner";
import { Heart, ShoppingCart, BadgeCheck, Star, Download, Loader2, Rotate3d, X, Smartphone, Printer, ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { API } from "@/lib/api";
import useRazorpay from "@/hooks/useRazorpay";
import { logTermsCheckpoint } from "@/lib/termsCheckpoint";
import FaqBlock, { FAQ_SLUGS } from "@/components/FaqBlock";
import ShareButtons from "@/components/ShareButtons";

function ShareBar({ id, title }) {
  return <ShareButtons type="listing" id={id} title={title} />;
}

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { add, has } = useCart();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [rzpBuying, setRzpBuying] = useState(false);
  const [owned, setOwned] = useState(false);
  const [wish, setWish] = useState(false);
  const [show3D, setShow3D] = useState(false);
  const { openCheckout } = useRazorpay();

  useEffect(() => {
    // Always land at the top of the page — previously the browser kept the
    // scroll position from the marketplace grid so the description block
    // showed first, hiding the price + Add-to-cart above the fold.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => nav("/marketplace"));
    if (user) {
      api.get("/orders").then((r) => setOwned(r.data.some((o) => o.listing_id === id)));
      api.get("/wishlist").then((r) => setWish(r.data.some((w) => w.id === id))).catch(() => {});
    }
  }, [id, user]);

  const buyRazorpay = async () => {
    if (!user) return nav("/login", { state: { from: `/design/${id}` } });
    setRzpBuying(true);
    try {
      const { data: order } = await api.post("/payments/razorpay/create-order", { listing_ids: [id] });
      const success = await openCheckout({
        order,
        name: "Jeweltix",
        description: `${l?.title || "Design"} · ₹${(order.amount / 100).toFixed(0)}`,
      });
      const { data: verified } = await api.post("/payments/razorpay/verify", success);
      if (verified?.success) {
        logTermsCheckpoint({
          role: "buyer",
          event: "purchase",
          contextId: verified.our_tx_id,
          checkboxes: ["buyer_terms", "privacy"],
        });
        toast.success("Payment successful — your design is ready.");
        setOwned(true);
        nav(`/payment/success?tx=${verified.our_tx_id}`);
      }
    } catch (err) {
      if (err?.code === "cancelled") toast.info("Payment cancelled.");
      else if (err?.code === "failed") toast.error(err.message || "Payment failed. Please try again.");
      else toast.error(apiErr(err) || err?.message || "Payment could not be completed");
    } finally {
      setRzpBuying(false);
    }
  };

  const toggleWish = async () => {
    if (!user) return nav("/login");
    const { data } = await api.post(`/wishlist/${id}`);
    setWish(data.wishlisted);
    toast.success(data.wishlisted ? "Saved to wishlist" : "Removed from wishlist");
  };

  if (!l) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading design…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
      {show3D && (
        <div
          className="fixed inset-0 z-[70] bg-slate-950/90 backdrop-blur-sm flex flex-col p-4 sm:p-8"
          data-testid="cad-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShow3D(false);
          }}
        >
          <div className="flex items-center justify-between mb-3 max-w-6xl mx-auto w-full">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-primary/80">
                Interactive 3D Preview
              </p>
              <p className="font-display font-semibold text-white text-sm sm:text-base">{l.title}</p>
            </div>
            <button
              onClick={() => setShow3D(false)}
              data-testid="close-3d-preview-btn"
              className="rounded-full p-2 bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Close 3D preview"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0 max-w-6xl mx-auto w-full">
            <CadViewer
              listingId={id}
              editable={l.has_3dm ?? l.editable}
              dmUrl={(l.has_3dm ?? l.editable) ? `${API}/listings/${id}/preview-3dm` : undefined}
            />
          </div>
        </div>
      )}
      <div className="text-xs text-muted-foreground mb-4 font-mono">
        <Link to="/marketplace" className="hover:text-primary">Marketplace</Link> / {l.category_name} / <span className="text-foreground">{l.title}</span>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        <div className="h-[420px] lg:h-[560px] flex flex-col gap-3">
          <div className="flex-1 min-h-0">
            <AngleGallery
              listing={l}
              fallbackNode={
                <CadViewer
                  listingId={id}
                  editable={l.has_3dm ?? l.editable}
                  dmUrl={(l.has_3dm ?? l.editable) ? `${API}/listings/${id}/preview-3dm` : undefined}
                />
              }
            />
          </div>
          {(l.angle_previews?.length || 0) > 0 && (
            <button
              type="button"
              onClick={() => setShow3D(true)}
              data-testid="open-3d-preview-btn"
              className="w-full flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground py-3 text-base font-bold shadow-md hover:bg-primary/90 hover:shadow-lg active:scale-[0.99] transition-all"
            >
              <Rotate3d className="h-5 w-5" /> View in 3D — rotate, zoom, inspect
              <span className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/80 ml-1">
                on demand
              </span>
            </button>
          )}
          <p className="text-xs text-muted-foreground font-mono">
            ● Watermarked preview mesh · original file delivered after purchase via secure link
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <FormatChips listing={l} />
            {l.featured && <span className="font-mono text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-1 rounded">Featured</span>}
            {l.product_id && (
              <span
                data-testid="pd-product-id"
                className="font-mono text-[10px] uppercase tracking-widest bg-secondary text-muted-foreground px-2 py-1 rounded"
                title="Jeweltix Product ID"
              >
                {l.product_id}
              </span>
            )}
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl leading-tight">{l.title}</h1>

          <Link to={`/designer/${l.seller?.id}`} className="inline-flex items-center gap-2 mt-3 text-sm text-muted-foreground hover:text-primary">
            <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{l.seller?.name?.[0]}</div>
            {l.seller?.name} {l.seller?.verified && <BadgeCheck className="h-4 w-4 text-primary" />}
            <span className="font-mono">· ★ {l.seller?.rating || "—"}</span>
          </Link>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 border border-border rounded-md p-4 bg-card">
            <Spec label="Formats" value={(l.formats && l.formats.length ? l.formats.join(" · ") : l.file_type).toUpperCase()} />
            <Spec label="File Size" value={fileSize(l.file_size)} />
            <Spec label="Polygons" value={`${(l.poly_count / 1000).toFixed(1)}K`} />
            <Spec label="CAM / Wax Wt." value={l.cam_weight ? `${l.cam_weight} g` : "—"} />
            <Spec label="Ring Size" value={l.ring_size_indian || l.ring_size || "—"} />
            <Spec label="Editable (3DM)" value={l.has_3dm ?? l.editable ? "Yes" : "No"} />
            {l.length_mm > 0 && (
              <Spec
                label="Dimensions (L × W × H)"
                value={`${l.length_mm} × ${l.width_mm} × ${l.height_mm} mm`}
                data-testid="listing-dimensions"
              />
            )}
            {l.ring_inner_diameter_mm && (
              <Spec label="Ring Inner Ø" value={`${l.ring_inner_diameter_mm} mm${l.ring_size_indian ? ` · Size ${l.ring_size_indian} (IN)` : ""}`} />
            )}
            {l.bangle_inner_diameter_mm && (
              <Spec label="Bangle Inner Ø" value={`${l.bangle_inner_diameter_mm} mm${l.bangle_size_indian ? ` · ${l.bangle_size_indian}` : ""}`} />
            )}
            {l.max_thickness_mm > 0 && !l.ring_inner_diameter_mm && !l.bangle_inner_diameter_mm && (
              <Spec label="Max Thickness" value={`${l.max_thickness_mm} mm`} />
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
            Measurements are taken from the design file. Final manufactured dimensions may vary with finishing and metal shrinkage. Use the <b>Measure</b> tool in the 3D viewer to check any part of the design yourself.
          </p>

          {l.weights && l.weights.gold_18k && (
            <div className="mt-4 border rounded-md p-4" data-testid="weight-estimate" style={{ borderColor: "hsl(var(--gold) / 0.4)", background: "hsl(var(--gold) / 0.05)" }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--gold))" }}>Estimated Metal Weight</p>
                <span className="font-mono text-[10px] text-muted-foreground">approx · from {l.cam_weight} g wax</span>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center">
                {[["22K Gold", l.weights.gold_22k], ["18K Gold", l.weights.gold_18k], ["14K Gold", l.weights.gold_14k], ["Silver", l.weights.silver]].map(([lbl, val]) => (
                  <div key={lbl}>
                    <p className="font-display font-bold text-lg">{val}<span className="text-xs font-normal"> g</span></p>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{lbl}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-3">Estimated cast weight per metal to help you gauge manufacturing cost. Actual weight varies with finishing & stone setting.</p>
            </div>
          )}

          <div className="mt-6 border border-border rounded-md p-5 bg-card">
            <div className="flex items-baseline justify-between">
              <span className="font-display font-extrabold text-3xl">{inr(l.price)}</span>
              <span className="text-xs text-muted-foreground font-mono">incl. lifetime downloads</span>
            </div>
            {owned ? (
              <button onClick={() => nav("/buyer")} data-testid="owned-download-btn" className="mt-4 w-full flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-md py-3 font-semibold hover:bg-emerald-700">
                <Download className="h-4 w-4" /> You own this — Go to downloads
              </button>
            ) : (
              <>
                <p className="mt-3 text-[11px] text-muted-foreground leading-snug border-l-2 border-primary/40 pl-2" data-testid="pdp-purchase-consent">
                  By buying, you accept the <Link to="/terms/buyer" className="text-primary underline">Buyer Terms</Link>. Files are licensed for single-buyer use — resale or redistribution is not permitted.
                </p>
                <button
                  onClick={buyRazorpay}
                  disabled={rzpBuying}
                  data-testid="buy-now-razorpay"
                  className="mt-2 w-full flex items-center justify-center gap-2 rounded-md py-3 font-semibold text-white transition-transform hover:-translate-y-[1px] disabled:opacity-60"
                  style={{ background: "linear-gradient(90deg, #072654 0%, #0b3fa8 100%)" }}
                >
                  {rzpBuying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
                  {rzpBuying ? "Opening Razorpay…" : "Buy Now with Razorpay"}
                </button>
                <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest text-center mt-1">
                  UPI · Cards · Netbanking · Wallets
                </p>
              </>
            )}
            {!owned && (
              <button
                onClick={() => { if (has(id)) { nav("/cart"); } else { add(l); toast.success("Added to cart"); } }}
                data-testid="add-to-cart-btn"
                className="mt-2 w-full flex items-center justify-center gap-2 rounded-md py-2.5 border text-sm font-medium transition-colors border-border hover:border-primary"
              >
                <ShoppingCart className="h-4 w-4" /> {has(id) ? "In cart — view cart" : "Add to cart"}
              </button>
            )}
            <button onClick={toggleWish} data-testid="wishlist-btn" className={`mt-2 w-full flex items-center justify-center gap-2 rounded-md py-2.5 border text-sm font-medium transition-colors ${wish ? "border-primary text-primary bg-primary/5" : "border-border hover:border-primary"}`}>
              <Heart className={`h-4 w-4 ${wish ? "fill-primary" : ""}`} /> {wish ? "Saved" : "Save to wishlist"}
            </button>
          </div>

          <ShareBar id={id} title={l.title} />
        </div>
      </div>

      {/* PRINT TO REALITY — service promo (aspirational, not "coming soon") */}
      <PrintToRealityPromo />

      {/* Contextual FAQs — CAM vendors + printing (schema.org Question emitted per row) */}
      <FaqBlock title="About Print to Reality" slugs={FAQ_SLUGS.camVendors} testid="pdp-faq-cam" />

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 mt-10">
        <div>
          <h2 className="font-display font-semibold text-xl mb-3">Description</h2>
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{l.description}</p>
          {l.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {l.tags.map((t) => <span key={t} className="font-mono text-xs bg-secondary px-2.5 py-1 rounded">#{t}</span>)}
            </div>
          )}

          <h2 className="font-display font-semibold text-xl mt-10 mb-3">Reviews ({l.reviews?.length || 0})</h2>
          {(!l.reviews || l.reviews.length === 0) ? (
            <p className="text-sm text-muted-foreground">No reviews yet. Only verified buyers can review.</p>
          ) : (
            <div className="space-y-3">
              {l.reviews.map((r) => (
                <div key={r.id} className="border border-border rounded-md p-4 bg-card">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{r.buyer_name}</span>
                    <span className="flex items-center gap-0.5 text-amber-500">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-500" />)}</span>
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contextual FAQs — file formats (3DM vs STL, printing formats) */}
      <FaqBlock title="File formats — 3DM vs STL" slugs={FAQ_SLUGS.fileFormats} testid="pdp-faq-formats" />

      {/* Sticky mobile buy bar — the price + CTA used to sit two scrolls
          below the fold on a phone. Pinned to the thumb zone instead,
          with iOS safe-area padding. */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md px-3 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] flex items-center gap-3"
        data-testid="mobile-buy-bar"
      >
        <div className="min-w-0">
          <p className="font-display font-extrabold text-xl leading-none" data-testid="mobile-buy-bar-price">{inr(l.price)}</p>
          <p className="text-[10px] text-muted-foreground font-mono truncate">lifetime downloads</p>
        </div>
        {owned ? (
          <button
            onClick={() => nav("/buyer")}
            data-testid="mobile-buy-bar-download"
            className="ml-auto flex-1 max-w-[62%] flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-full py-3 text-sm font-bold"
          >
            <Download className="h-4 w-4" /> Downloads
          </button>
        ) : (
          <div className="ml-auto flex items-center gap-2 flex-1 max-w-[68%]">
            <button
              onClick={() => { if (has(id)) { nav("/cart"); } else { add(l); toast.success("Added to cart"); } }}
              data-testid="mobile-buy-bar-cart"
              aria-label={has(id) ? "View cart" : "Add to cart"}
              className={`shrink-0 h-11 w-11 flex items-center justify-center rounded-full border ${has(id) ? "border-primary text-primary bg-primary/5" : "border-border"}`}
            >
              <ShoppingCart className="h-4 w-4" />
            </button>
            <button
              onClick={buyRazorpay}
              disabled={rzpBuying}
              data-testid="mobile-buy-bar-buy"
              className="flex-1 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-white disabled:opacity-60"
              style={{ background: "linear-gradient(90deg, #072654 0%, #0b3fa8 100%)" }}
            >
              {rzpBuying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
              {rzpBuying ? "Opening…" : "Buy Now"}
            </button>
          </div>
        )}
      </div>
      {/* Spacer so the bar never covers the last row of content */}
      <div className="lg:hidden h-20" aria-hidden="true" />
    </div>
  );
}

/** PrintToRealityPromo — sits below the buy panel on every product page.
 *  Marketing tone (no "coming soon" language): pitches wax-printing as if it's
 *  already available, drives buyers to browse partner shops and print shops to
 *  join. Uses the same emerald palette as the homepage vendor CTA. */
function PrintToRealityPromo() {
  return (
    <section
      data-testid="print-to-reality-promo"
      className="mt-10 relative overflow-hidden rounded-xl border border-emerald-500/25 text-white p-6 sm:p-8"
      style={{ background: "linear-gradient(135deg, #052e3f 0%, #084a68 55%, #0b6787 100%)" }}
    >
      {/* subtle grid + glow — CSS only */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "34px 34px",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(closest-side, #34d399, transparent)" }}
      />

      <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300 mb-3">
            <Printer className="h-3.5 w-3.5" /> Print to Reality
          </div>
          <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight max-w-xl">
            Turn this design into a physical wax pattern — printed by a verified shop near you.
          </h3>
          <p className="mt-3 text-sm text-slate-200 leading-relaxed max-w-lg">
            Skip the offline hunt. After purchase, we route your STL to a nearby 3D print vendor with matching machinery, quote you upfront, and hold your payment in escrow until the piece is in your hand.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/vendor/apply"
              data-testid="promo-partner-cta"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2.5 font-semibold text-sm transition-colors"
            >
              Explore print shops <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 rounded-md border border-slate-500/60 hover:border-emerald-300 hover:text-emerald-200 px-5 py-2.5 font-semibold text-sm transition-colors"
            >
              How it works
            </Link>
          </div>
        </div>

        <div className="grid gap-3">
          <PromoBullet icon={MapPin} title="Nearby, not shipped from afar" body="Match to a print shop in your city — cut turnaround from days to hours." />
          <PromoBullet icon={ShieldCheck} title="Escrow-protected settlement" body="Vendor gets paid only after your piece is delivered. 48-hour hold, then release." />
          <PromoBullet icon={Printer} title="SLA · DLP · Wax-Jet" body="Filter by machine type so your ring, pendant or bangle prints on the right rig." />
        </div>
      </div>
    </section>
  );
}

function PromoBullet({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-3 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm p-3">
      <div className="h-8 w-8 shrink-0 rounded-md bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
        <Icon className="h-4 w-4 text-emerald-300" strokeWidth={1.7} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight">{title}</p>
        <p className="text-[11px] text-slate-300 mt-0.5 leading-snug">{body}</p>
      </div>
    </div>
  );
}
